<script setup>
import SkeletonPlaceholder from '@/components/Base/SkeletonPlaceholder.vue'

// Define props
const props = defineProps({
    shopHeader: {
        type: Object,
        default: () => ({}),
        validator(rawValue) {
            const validations = {
                name: rawValue => rawValue.name === undefined || typeof rawValue.name === 'string',
                imageUrl: rawValue => rawValue.logoType?.url === undefined || typeof rawValue.logoType?.url === 'string',
                altText: rawValue => rawValue.logoType?.altText === undefined || typeof rawValue.logoType?.altText === 'string',
                placeholder: rawValue => rawValue.placeholder === undefined || typeof rawValue.placeholder === 'boolean'
            }

            return Object.entries(validations).every(([_, validate]) => validate(rawValue))
        }
    }
})

// Markdown
const { $md } = useNuxtApp()
const shopName = computed(() => $md.renderInline(props.shopHeader?.name || ''))
</script>

<template>
    <section class="shop-header">
        <div v-if="shopHeader.placeholder" class="shop-logo">
            <component
                :is="SkeletonPlaceholder"
                :skeleton-placeholder-config="{ type: 'image', width: '100%', height: '150px' }"
            />
        </div>

        <picture v-else-if="isTypeImage(shopHeader.logoType?.url)">
            <RespImgSrc
                tag-type="source"
                :image-src="shopHeader.logoType?.url"
                :srcset="[150, 200, 300]"
                :format="['avif', 'webp', 'jpg']"
                :width="300"
                :height="300"
                sizes="(min-width: 1024px) 300px, (min-width: 768px) 200px, 150px"
                :additional-attrs="{ alt: shopHeader.logoType?.altText }"
            />
            <RespImgSrc
                tag-type="img"
                class="shop-logo"
                :image-src="shopHeader.logoType?.url"
                :srcset="[150, 200, 300]"
                :format="['jpg']"
                :width="300"
                :height="300"
                sizes="(min-width: 1024px) 300px, (min-width: 768px) 200px, 150px"
                :additional-attrs="{ alt: shopHeader.logoType?.altText }"
            />
        </picture>

        <h1 v-if="shopName" class="shop-name" v-html="shopName" />
    </section>
</template>

<style lang="scss">
.shop-header {
    margin-block-end: 40px;

    @include bp(medium1) {
        margin-block-end: 70px;
    }
}

.shop-logo {
    display: block;
    max-width: 150px;
    max-height: 150px;
    margin: 0 auto 20px;
    width: auto;
    height: auto;

    @include bp(medium1) {
        max-width: 200px;
        max-height: 200px;
    }

    @include bp(large1) {
        max-width: 300px;
    }
}

.shop-name {
    font-weight: 800;
    font-size: px2rem(14);
    line-height: px2rem(18);
    color: $shop-name;
    text-align: center;
    margin: 0;

    @include bp(medium1) {
        font-size: px2rem(20);
        line-height: px2rem(25);
    }
}
</style>
